import React from "react";
import { useReveal } from "@/lib/useReveal";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * Roadmap - serpentine zigzag timeline.
 * Numbered items alternate left/right with a continuous gradient border
 * (Leadmato green -> coral -> purple) connecting them.
 * Every item is either shipped, actively being built, or on the near-term
 * backlog. Zero vaporware.
 */

const GRADIENT = "linear-gradient(to right, #0F5535 0%, #D95A40 50%, #7A3EA5 100%)";

const ITEMS = [
  {
    status: "live",
    title: "Multi-source scans, one query",
    body: "Type what you sell — Leadmato pulls signals from Reddit, forums, LinkedIn, X, YouTube, Instagram, Facebook, TikTok, plus open web and news, in a single run. You choose which platforms run on each scan. Public content only.",
  },
  {
    status: "live",
    title: "The four-lens engine",
    body: "Every scan runs through Find Customers, Pain Points, Market Analysis and Partners at once — one query, four answers — and the best signals save straight to your pipeline.",
  },
  {
    status: "live",
    title: "Pick your platforms per scan",
    body: "Every lens pre-selects the platforms that fit it, but you can tick or untick any before you run — so you're always in control of where we look.",
  },
  {
    status: "live",
    title: "Intent scoring, transparent reasoning",
    body: "Every signal ranked 0–100 by buying intent + persona fit, with the matched keywords surfaced so you know why it scored high. Sellers, off-topic and stale posts are filtered out before you ever see them.",
  },
  {
    status: "live",
    title: "Save leads to a kanban board",
    body: "Push the best signals to New → Contacted → Replied → Closed, with notes, tags and the source link kept on every lead.",
  },
  {
    status: "live",
    title: "Market-scope onboarding + first-scan safety net",
    body: "Pick Local City / All India / Global up front. If your first scan returns zero results, we refund the scan and suggest broadening the scope automatically.",
  },
  {
    status: "live",
    title: "Team workspaces",
    body: "Invite teammates, assign roles (owner, admin, member), and switch between workspaces — everyone sees the same saved leads and scan history.",
  },
  {
    status: "building",
    title: "Scaling to more business categories",
    body: "This is what we're working on next. Leadmato is tuned per industry today — we're deepening coverage so many more business types, and deeper niches inside them, get the same buyer-grade results out of the box.",
  },
  {
    status: "building",
    title: "CSV export + shareable scan links",
    body: "Bulk-export leads and full scan results. Deep-link a scan URL so your team lands on the same ranked board without hunting.",
  },
  {
    status: "planned",
    title: "Chrome extension",
    body: "And then this: reply to Leadmato-surfaced signals directly on Reddit, X and LinkedIn from a browser extension — no context switch, no copy-paste, no losing your place.",
  },
  {
    status: "planned",
    title: "Developer API",
    body: "Public REST endpoints and workspace-scoped keys so you can run scans and pull leads straight from your own code. Coming soon.",
  },
  {
    status: "planned",
    title: "Slack + Discord lead alerts",
    body: "Pipe new high-intent leads straight into your team channel via webhook. See buying signals in the same place you already work.",
  },
];

const STATUS_MAP = {
  live: { label: "Live now", color: "#0F5535", tint: "#DFF0E6" },
  building: { label: "Building this week", color: "#D95A40", tint: "#FCE4DB" },
  planned: { label: "On the roadmap", color: "#7A3EA5", tint: "#EEE1F7" },
};

export default function Roadmap() {
  const [head, headVisible] = useReveal();

  return (
    <section id="roadmap" className="relative lm-cv py-20 md:py-28 bg-lm-bg overflow-hidden">
      {/* Ambient dot texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.3]" aria-hidden>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(15,85,53,0.12) 1px, transparent 0)",
            backgroundSize: "36px 36px",
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 md:px-10">
        <div
          ref={head}
          className={`max-w-3xl mb-14 md:mb-16 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            The road ahead
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-semibold leading-[1.08] tracking-tight text-lm-text">
            Public roadmap. No vaporware.
          </h2>
          <p className="mt-4 text-base md:text-lg text-lm-text2 leading-relaxed">
            Everything below is either shipped, being built right now, or next in the queue.
            Follow the path — every step is real work you can already use or will see land soon.
          </p>
        </div>

        {/* The zigzag timeline */}
        <div className="relative max-w-xl md:max-w-2xl mx-auto">
          {ITEMS.map((item, i) => (
            <TimelineStep key={item.title} item={item} index={i} total={ITEMS.length} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl border border-lm-border bg-lm-surface p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 max-w-3xl mx-auto">
          <div className="w-12 h-12 rounded-xl bg-lm-tint2 text-lm-primary flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-lm-text mb-1">
              Missing something you need?
            </h3>
            <p className="text-sm text-lm-text2 leading-relaxed">
              We ship what customers actually ask for. Every reasonable request gets a real reply
              from the founder — usually the same day.
            </p>
          </div>
          <a
            href="mailto:piyush@winmassiveimpact.com?subject=Feature request"
            data-testid="roadmap-suggest-btn"
            className="inline-flex items-center gap-1.5 bg-lm-text text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-lm-primary transition-colors shrink-0"
          >
            Suggest a feature
            <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </a>
        </div>
      </div>
    </section>
  );
}

function TimelineStep({ item, index, total }) {
  const [ref, visible] = useReveal();
  const odd = index % 2 === 0; // step 1 (index 0) is "odd" per source CSS
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const status = STATUS_MAP[item.status] || STATUS_MAP.planned;

  // Border configuration mirrors the source CSS - odd items have right border,
  // even items have left border, all share the top gradient border.
  const style = {
    borderImage: GRADIENT,
    borderImageSlice: 1,
    borderTopStyle: isFirst ? "none" : "solid",
    borderTopWidth: isFirst ? 0 : 3,
    borderRightStyle: odd ? "solid" : "none",
    borderRightWidth: odd ? 3 : 0,
    borderLeftStyle: odd ? "none" : "solid",
    borderLeftWidth: odd ? 0 : 3,
    borderBottomStyle: "none",
    borderBottomWidth: 0,
  };

  return (
    <div
      ref={ref}
      data-testid="roadmap-step"
      className={`relative py-8 md:py-10 ${odd ? "pl-0 pr-8 md:pr-12" : "pr-0 pl-8 md:pl-12"} lm-reveal ${
        visible ? "is-visible" : ""
      }`}
      style={{ ...style, transitionDelay: `${index * 60}ms` }}
    >
      {/* Numbered circle - alternates sides */}
      <div
        className={`absolute top-6 md:top-8 z-10 flex items-center justify-center rounded-full w-11 h-11 md:w-12 md:h-12 text-white font-display font-bold text-sm md:text-base shadow-[0_10px_24px_-10px_rgba(0,0,0,0.35)] ${
          odd ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2"
        }`}
        style={{
          background: `linear-gradient(135deg, ${status.color}, ${status.color}cc)`,
        }}
        aria-hidden
      >
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className={odd ? "pr-2" : "pl-2"}>
        {/* Status pill */}
        <div
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold mb-3"
          style={{ backgroundColor: status.tint, color: status.color }}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${item.status === "building" ? "animate-pulse" : ""}`}
            style={{ backgroundColor: status.color }}
          />
          {status.label}
        </div>

        <h3 className="text-lg md:text-xl font-display font-semibold text-lm-text leading-tight">
          {item.title}
        </h3>
        <p className="mt-2 text-sm md:text-[15px] text-lm-text2 leading-relaxed">
          {item.body}
        </p>
      </div>
    </div>
  );
}
